/** Minimal, dependency-free math for the pure simulation core. 1 unit = 1 metre. */

export interface Vec2 {
  x: number;
  z: number;
}
export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

export const GRAVITY = 9.81;
export const TAU = Math.PI * 2;
/** Speed of sound in air at sea level (m/s) — drives delayed distant impact audio. */
export const SPEED_OF_SOUND = 343;

export const vec3 = (x = 0, y = 0, z = 0): Vec3 => ({ x, y, z });
export const clone3 = (v: Vec3): Vec3 => ({ x: v.x, y: v.y, z: v.z });

export const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);
export const clamp01 = (v: number): number => clamp(v, 0, 1);
export const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
export const invLerp = (a: number, b: number, v: number): number => (b === a ? 0 : (v - a) / (b - a));
/** Maps v from [inA,inB] into [outA,outB], clamped. */
export const remap = (v: number, inA: number, inB: number, outA: number, outB: number): number =>
  lerp(outA, outB, clamp01(invLerp(inA, inB, v)));
export const smoothstep = (t: number): number => {
  const x = clamp01(t);
  return x * x * (3 - 2 * x);
};

/** Frame-rate independent exponential approach. `rate` = fraction remaining after 1s. */
export const damp = (a: number, b: number, rate: number, dt: number): number =>
  b + (a - b) * Math.pow(rate, dt);

export const dist2D = (ax: number, az: number, bx: number, bz: number): number =>
  Math.hypot(ax - bx, az - bz);

export const dist3 = (a: Vec3, b: Vec3): number => Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);

export const len3 = (v: Vec3): number => Math.hypot(v.x, v.y, v.z);

export const norm3 = (v: Vec3): Vec3 => {
  const l = Math.hypot(v.x, v.y, v.z);
  return l > 1e-9 ? { x: v.x / l, y: v.y / l, z: v.z / l } : { x: 0, y: 0, z: 0 };
};

export const add3 = (a: Vec3, b: Vec3): Vec3 => ({ x: a.x + b.x, y: a.y + b.y, z: a.z + b.z });
export const sub3 = (a: Vec3, b: Vec3): Vec3 => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z });
export const scale3 = (a: Vec3, s: number): Vec3 => ({ x: a.x * s, y: a.y * s, z: a.z * s });
export const dot3 = (a: Vec3, b: Vec3): number => a.x * b.x + a.y * b.y + a.z * b.z;

export const cross3 = (a: Vec3, b: Vec3): Vec3 => ({
  x: a.y * b.z - a.z * b.y,
  y: a.z * b.x - a.x * b.z,
  z: a.x * b.y - a.y * b.x,
});

/** Compass-style heading in radians from a direction on the XZ plane (0 = +Z / north). */
export const headingOf = (dx: number, dz: number): number => Math.atan2(dx, dz);

/** Direction vector on XZ from a heading in radians. */
export const dirFromHeading = (h: number): Vec2 => ({ x: Math.sin(h), z: Math.cos(h) });

/** Shortest signed angular difference a→b, in (-π, π]. */
export const angleDelta = (a: number, b: number): number => {
  let d = (b - a) % TAU;
  if (d > Math.PI) d -= TAU;
  if (d <= -Math.PI) d += TAU;
  return d;
};

/** Rotate a heading toward a target by at most `maxStep` radians. */
export const turnToward = (current: number, target: number, maxStep: number): number => {
  const d = angleDelta(current, target);
  return current + clamp(d, -maxStep, maxStep);
};

export const degToRad = (d: number): number => (d * Math.PI) / 180;
export const radToDeg = (r: number): number => (r * 180) / Math.PI;

/**
 * Closest approach of a moving point to a stationary point over [0, dt].
 * Used by swept projectile collision. Returns { t, dist } with t in [0, dt].
 */
export function closestApproach(
  from: Vec3,
  vel: Vec3,
  target: Vec3,
  dt: number,
): { t: number; dist: number } {
  const rx = from.x - target.x;
  const ry = from.y - target.y;
  const rz = from.z - target.z;
  const vv = vel.x * vel.x + vel.y * vel.y + vel.z * vel.z;
  if (vv < 1e-12) return { t: 0, dist: Math.hypot(rx, ry, rz) };
  let t = -(rx * vel.x + ry * vel.y + rz * vel.z) / vv;
  t = clamp(t, 0, dt);
  const dx = rx + vel.x * t;
  const dy = ry + vel.y * t;
  const dz = rz + vel.z * t;
  return { t, dist: Math.hypot(dx, dy, dz) };
}

/**
 * Ballistic lead solution: where to aim to hit a target moving at constant velocity
 * with a projectile of constant speed. Returns the intercept point, or null if
 * the projectile cannot catch the target.
 */
export function interceptPoint(
  shooter: Vec3,
  target: Vec3,
  targetVel: Vec3,
  projectileSpeed: number,
): Vec3 | null {
  const rx = target.x - shooter.x;
  const ry = target.y - shooter.y;
  const rz = target.z - shooter.z;
  const a = dot3(targetVel, targetVel) - projectileSpeed * projectileSpeed;
  const b = 2 * (rx * targetVel.x + ry * targetVel.y + rz * targetVel.z);
  const c = rx * rx + ry * ry + rz * rz;

  let t: number;
  if (Math.abs(a) < 1e-6) {
    if (Math.abs(b) < 1e-9) return null;
    t = -c / b;
  } else {
    const disc = b * b - 4 * a * c;
    if (disc < 0) return null;
    const sq = Math.sqrt(disc);
    const t1 = (-b - sq) / (2 * a);
    const t2 = (-b + sq) / (2 * a);
    const valid = [t1, t2].filter((v) => v > 0);
    if (valid.length === 0) return null;
    t = Math.min(...valid);
  }
  if (!Number.isFinite(t) || t <= 0) return null;
  return {
    x: target.x + targetVel.x * t,
    y: target.y + targetVel.y * t,
    z: target.z + targetVel.z * t,
  };
}

/**
 * Launch velocity for a mortar-style lob from `from` to `to`.
 *
 * Solves the low-angle arc rather than the high one: a flatter shot spends less time in
 * the air, which matters because the target is a submarine that keeps moving while the
 * charge flies. Falls back to a 45-degree throw if the range is beyond what the chosen
 * speed can reach, so the projectile always leaves the tube in a sane direction.
 */
export function lobVelocity(from: Vec3, to: Vec3, speed = 110): Vec3 {
  const dx = to.x - from.x;
  const dz = to.z - from.z;
  const horizontal = Math.hypot(dx, dz);
  const dy = to.y - from.y;
  const g = GRAVITY;
  const v2 = speed * speed;
  const disc = v2 * v2 - g * (g * horizontal * horizontal + 2 * dy * v2);
  const angle = disc < 0
    ? Math.PI / 4
    : Math.atan((v2 - Math.sqrt(disc)) / (g * horizontal));
  const flat = Math.max(horizontal, 1e-6);
  return {
    x: (dx / flat) * Math.cos(angle) * speed,
    y: Math.sin(angle) * speed,
    z: (dz / flat) * Math.cos(angle) * speed,
  };
}
